// Custom donation-amount presets: pure helpers over the backend `DonationAmountPresets` defaults and
// bounds, which are generated into constants.gen.ts. The public donation form and the admin editor both
// read these. The SERVER is the authoritative guardrail; these are for immediate client feedback, never
// the source of truth for a persisted write.

import {
  DEFAULT_DONATION_AMOUNTS,
  DONATION_AMOUNT_MAX,
  DONATION_AMOUNT_MAX_COUNT,
  DONATION_AMOUNT_MIN,
} from '../generated/constants.gen';

/**
 * The stored → served resolution: a non-empty custom set is used verbatim (in the admin's order); an
 * empty or absent set falls back to {@link DEFAULT_DONATION_AMOUNTS} so the form is never button-less.
 * Mirrors the backend `DonationAmountPresets.Resolve`.
 */
export function resolveDonationAmounts(
  custom: readonly number[] | null | undefined,
): number[] {
  return custom && custom.length > 0 ? [...custom] : [...DEFAULT_DONATION_AMOUNTS];
}

/**
 * Editor-side validation mirroring the backend `DonationAmountPresets.Validate`. Returns a
 * treasurer-readable message for the first violated rule, or `null` when the set is valid. An empty set
 * is valid — it clears the customs so the public page falls back to the defaults. Non-finite / non-integer
 * entries (including a blank editor row parsed to `NaN`) fail the whole-dollar rule. The server re-checks
 * authoritatively.
 */
export function donationAmountsError(amounts: readonly number[]): string | null {
  if (amounts.length > DONATION_AMOUNT_MAX_COUNT) {
    return `Enter at most ${DONATION_AMOUNT_MAX_COUNT} donation amounts.`;
  }

  const seen = new Set<number>();
  for (const amount of amounts) {
    if (!Number.isInteger(amount)) {
      return 'Each donation amount must be a whole dollar (no cents).';
    }
    if (amount < DONATION_AMOUNT_MIN || amount > DONATION_AMOUNT_MAX) {
      return `Each donation amount must be between $${DONATION_AMOUNT_MIN} and $${DONATION_AMOUNT_MAX.toLocaleString()}.`;
    }
    if (seen.has(amount)) {
      return 'Donation amounts must not repeat.';
    }
    seen.add(amount);
  }

  return null;
}
